import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Stack,
    Tooltip,
    IconButton,
    Snackbar,
    Alert,
    TextField,
    Autocomplete,
    Button,
    Chip,
    Tabs,
    Tab,
    InputAdornment,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import {
    useLazyGetBranchCOCReportQuery,
    useLazyGetBranchCOCProvinceReportQuery,
} from "store/services/reportApi";
import {
    useLazyGetDDLBranchListQuery,
    useLazyGetBranchByRegionDDLQuery,
} from "store/services/branchApi";
import { useLazyGetDdlRegionListQuery } from "store/services/LocationsApi/regionApi";
import BaseSelect from "components/Shared/Base/BaseSelect";
import {
    Person,
    StorefrontOutlined,
    ChevronRightOutlined,
    ArrowBackIosNewOutlined,
    Search as SearchIcon,
    Visibility,
} from "@mui/icons-material";
import { useAppMeta } from "hooks";

const MODE_TABS = [
    { label: "Province Mode", value: "provinces" },
    { label: "Branch List Mode", value: "branches" },
];

const BranchCOCReport = () => {
    const { isVendor, vendorId, viewEntity } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

const [sortBy, setSortBy] = useState(
  searchParams.get("sortBy") || "branchName:1"
);
        const [branch, setBranch] = useState(searchParams.get("branch") || "all");
    const [region, setRegion] = useState(searchParams.get("region") || "all");

    const mode = searchParams.get("mode") || "provinces";
    const pmLevel = searchParams.get("pmLevel") || "provinces";
    const pmProvinceId = searchParams.get("pmProvinceId") || "";
    const pmProvinceName = searchParams.get("pmProvinceName") || "";
    const [provinceSortBy, setProvinceSortBy] = useState(
        searchParams.get("provinceSortBy") || "name:1"
    );
    const [provinceSearch, setProvinceSearch] = useState("");

    const [errorMessage, setErrorMessage] = useState("");

    const REPORT_HOME_PATH = "/app/crm/reports/coc-branch-report";

    const breadcrumbs =
        mode === "provinces" && pmLevel === "branches"
            ? [
                  { title: "Reports", path: "/app/crm/reports/list" },
                  {
                      title: "Pending Branch COC Report",
                      path: REPORT_HOME_PATH,
                  },
                  { title: pmProvinceName || "Province" },
              ]
            : [
                  { title: "Reports", path: "/app/crm/reports/list" },
                  { title: "Pending Branch COC Report" },
              ];

    const [
        fetchReport,
        {
            data: reportData,
            isLoading,
            isFetching,
            error: reportError,
        },
    ] = useLazyGetBranchCOCReportQuery();

    const [fetchBranches, { data: branchesData, isLoading: isBranchesLoading }] =
        useLazyGetDDLBranchListQuery();

    const [
        fetchBranchesByRegion,
        {
            data: branchesByRegionData,
            isLoading: isBranchesByRegionLoading,
            error: branchesByRegionError,
        },
    ] = useLazyGetBranchByRegionDDLQuery();

    const [fetchRegions, { data: regionsData, isLoading: isRegionsLoading }] =
        useLazyGetDdlRegionListQuery();

    const [
        fetchProvinceReport,
        {
            data: provinceData,
            isLoading: isProvinceLoading,
            isFetching: isProvinceFetching,
            error: provinceError,
        },
    ] = useLazyGetBranchCOCProvinceReportQuery();

    // Load region and branch dropdown data (branch-level views only)
    useEffect(() => {
        if (mode === "provinces" && pmLevel !== "branches") return;
        const accountId = viewEntity?.entity?.account?._id;
        const entityId = viewEntity?.entity?._id;
        if (!accountId || !entityId) return;

        fetchRegions({ account: accountId, entity: entityId });
        if (region === "all" || region === "none") {
            fetchBranches({ account: accountId, entity: entityId });
        } else if (region && typeof region === "string") {
            fetchBranchesByRegion({ regionId: region, account: accountId, entity: entityId });
        } else {
            setErrorMessage("Invalid region selection. Please try again.");
        }
    }, [fetchBranches, fetchBranchesByRegion, fetchRegions, viewEntity, region, mode, pmLevel]);

    // Restore province scoping when arriving from a breadcrumb back-link that
    // carries pmProvinceId but not the legacy region param
    useEffect(() => {
        if (
            mode === "provinces" &&
            pmLevel === "branches" &&
            pmProvinceId &&
            region === "all"
        ) {
            setRegion(pmProvinceId);
        }
    }, [mode, pmLevel, pmProvinceId, region]);

    const regionOptions = [
        { label: "All Provinces", value: "all" },
        ...(regionsData?.map((r) => ({
            label: r.label || r.name,
            value: r.value || r._id,
        })) || []),
    ];

    const branchOptions = [
        { label: "All Branches", value: "all" },
        ...(region === "all" || region === "none"
            ? branchesData?.map((b) => ({
                label: b.label || b.name,
                value: b.value || b._id,
            })) || []
            : branchesByRegionData?.data?.map((b) => ({
                label: b.name,
                value: b._id,
            })) || []),
    ];

    const fetchReportData = useCallback(() => {
        if (mode === "provinces" && pmLevel !== "branches") return;
        const [field, direction] = sortBy.split(":");
        const sortedBy = ["branchName", "totalNotPaid"].includes(field) ? field : "branchName";
        const sortDirection = ["1", "-1"].includes(direction) ? direction : "1";

        const newParams = new URLSearchParams(searchParams);
        newParams.set("page", currentPage.toString());
        newParams.set("limit", perPage.toString());
        if (branch !== "all") newParams.set("branch", branch);
        else newParams.delete("branch");
        if (region !== "all") newParams.set("region", region);
        else newParams.delete("region");
        newParams.set("sortBy", sortBy);
        setSearchParams(newParams, { replace: true });

        fetchReport({
            params: {
                page: currentPage,
                limit: perPage,
                account: viewEntity?.entity?.account?._id,
                entity: viewEntity?.entity?._id,
                branch: branch !== "all" ? branch : undefined,
                region: region !== "all" ? region : undefined,
                sortedBy,
                sortDirection,
                ...(isVendor && { vendorId }),
            },
        }).catch((err) => {
            setErrorMessage(err?.data?.message || "Failed to fetch Pending Branch COC Report");
            console.error("Pending Branch COC Report Error:", err);
        });
    }, [
        mode,
        pmLevel,
        sortBy,
        branch,
        region,
        currentPage,
        perPage,
        viewEntity,
        isVendor,
        vendorId,
        fetchReport,
        searchParams,
        setSearchParams,
    ]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    useEffect(() => {
        if (branchesByRegionError) setErrorMessage("Failed to fetch branches for the selected region.");
        if (reportError) setErrorMessage("Failed to fetch Pending Branch COC Report.");
    }, [branchesByRegionError, reportError]);

    useEffect(() => {
        if (mode !== "provinces" || pmLevel !== "provinces") return;
        const [field, direction] = provinceSortBy.split(":");
        fetchProvinceReport({
            params: {
                account: viewEntity?.entity?.account?._id,
                entity: viewEntity?.entity?._id,
                sortedBy: field,
                sortDirection: direction,
            },
        }).catch((err) => {
            setErrorMessage(
                err?.data?.message ||
                    "Failed to fetch Pending Branch COC Province Summary"
            );
            console.error(
                "Pending Branch COC Province Summary Error:",
                err
            );
        });
    }, [mode, pmLevel, provinceSortBy, viewEntity, fetchProvinceReport]);

    const handleModeChange = (_, newValue) => {
        if (!newValue || newValue === mode) return;
        setBranch("all");
        setRegion("all");
        setSearchParams({ mode: newValue }, { replace: true });
    };

    const handleDrillIntoBranches = (row) => {
        const provinceId = row.isUnassigned
            ? "none"
            : row.provinceId?.toString() || "";
        setRegion(provinceId);
        setBranch("all");
        const newParams = new URLSearchParams();
        newParams.set("mode", "provinces");
        newParams.set("pmLevel", "branches");
        newParams.set("pmProvinceId", provinceId);
        newParams.set("pmProvinceName", row.name || "");
        setSearchParams(newParams, { replace: true });
    };

    const handleBackToProvinces = () => {
        setRegion("all");
        setBranch("all");
        setSearchParams({ mode: "provinces" }, { replace: true });
    };

    const provinceColumns = [
        {
            name: "Province",
            cell: (row) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.name || "N/A"}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                    >
                        {row.code || ""}
                    </Typography>
                </Box>
            ),
        },
        {
            name: "User Not Paid",
            selector: (row) => row.userNotPaid,
            cell: (row) => `Rs. ${(row.userNotPaid ?? 0).toFixed(2)}`,
        },
        {
            name: "Vendor Not Paid",
            selector: (row) => row.vendorNotPaid,
            cell: (row) => `Rs. ${(row.vendorNotPaid ?? 0).toFixed(2)}`,
        },
        {
            name: "Total Not Paid",
            selector: (row) => row.totalNotPaid,
            cell: (row) => `Rs. ${(row.totalNotPaid ?? 0).toFixed(2)}`,
        },
        {
            name: "Action",
            cell: (row) => (
                <Button
                    size="small"
                    variant="outlined"
                    endIcon={<ChevronRightOutlined />}
                    onClick={() => handleDrillIntoBranches(row)}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    View Branches
                </Button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "180px",
        },
    ];

    const filteredProvinceData = useMemo(() => {
        const rawData = provinceData?.data ?? [];
        if (!provinceSearch.trim()) return rawData;
        const lower = provinceSearch.trim().toLowerCase();
        return rawData.filter(
            (p) =>
                p?.name?.toLowerCase().includes(lower) ||
                p?.code?.toLowerCase().includes(lower)
        );
    }, [provinceData, provinceSearch]);

    const columns = [
        {
            name: "Branch Name",
            selector: (row) => row.branchName,
            cell: (row) => row.branchName || "N/A",
        },
        {
            name: "Province",
            selector: (row) => row.regionName,
            cell: (row) => row.regionName || "N/A",
        },
        {
            name: "User Not Paid",
            selector: (row) => row.userNotPaid,
            cell: (row) => `Rs. ${(row.userNotPaid ?? 0).toFixed(2)}`,
        },
        {
            name: "Vendor Not Paid",
            selector: (row) => row.vendorNotPaid,
            cell: (row) => `Rs. ${(row.vendorNotPaid ?? 0).toFixed(2)}`,
        },
        {
            name: "Total Not Paid",
            selector: (row) => row.totalNotPaid,
            cell: (row) => `Rs. ${(row.totalNotPaid ?? 0).toFixed(2)}`,
        },
        {
            name: "Action",
            cell: (row) => {
                const params = new URLSearchParams();
                params.set("branch", row._id);
                params.set("branchName", row.branchName || "");
                if (
                    mode === "provinces" &&
                    pmLevel === "branches" &&
                    pmProvinceId
                ) {
                    params.set("pmProvinceId", pmProvinceId);
                    params.set("pmProvinceName", pmProvinceName || "");
                }
                return (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View by User">
                            <IconButton
                                color="primary"
                                size="small"
                                onClick={() =>
                                    navigate(`/app/crm/reports/branch-coc-user-summary?${params.toString()}`)
                                }
                            >
                                <Person />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="View by Vendor">
                            <IconButton
                                color="primary"
                                size="small"
                                onClick={() =>
                                    navigate(`/app/crm/reports/branch-coc-vendor-summary?${params.toString()}`)
                                }
                            >
                                <StorefrontOutlined />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "120px",
        },
    ];

    const summaryMetrics = [
        { label: "Total Not Paid", value: (reportData?.totalNotPaid ?? 0).toFixed(2) },
        { label: "User Not Paid", value: (reportData?.totalUserNotPaid ?? 0).toFixed(2) },
        { label: "Vendor Not Paid", value: (reportData?.totalVendorNotPaid ?? 0).toFixed(2) },
    ];

    useAppMeta("Pending Branch COC Report");

    const renderProvincesView = () => (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    gap: 2,
                    alignItems: "center",
                    px: 2,
                    pt: 2,
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ flexShrink: 0 }}
                >
                    Pending Branch COC Report — Provinces
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                        gap: 2,
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    {[
                        {
                            label: "Total Not Paid",
                            value: (provinceData?.totalNotPaid ?? 0).toFixed(2),
                        },
                        {
                            label: "User Not Paid",
                            value: (
                                provinceData?.totalUserNotPaid ?? 0
                            ).toFixed(2),
                        },
                        {
                            label: "Vendor Not Paid",
                            value: (
                                provinceData?.totalVendorNotPaid ?? 0
                            ).toFixed(2),
                        },
                    ].map((metric, i) => (
                        <Box key={i}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {metric.label}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                                Rs. {metric.value}
                            </Typography>
                        </Box>
                    ))}
                    {provinceData?.unassigned?.totalNotPaid > 0 && (
                        <Box
                            sx={{
                                px: 1,
                                py: 0.5,
                                border: 1,
                                borderColor: "primary.main",
                                borderRadius: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                "&:hover": { backgroundColor: "action.hover" },
                            }}
                        >
                            <Typography variant="caption" color="primary" noWrap>
                                Unassigned Branches · Rs. {(provinceData.unassigned.totalNotPaid ?? 0).toFixed(2)}
                            </Typography>
                            <Tooltip title="View Unassigned Branches" arrow>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleDrillIntoBranches(provinceData.unassigned)}
                                >
                                    <Visibility fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    <BaseSelect
                        label="Sort By"
                        value={provinceSortBy}
                        onChange={(e) => setProvinceSortBy(e.target.value)}
                        options={[
                            { label: "Province Name (Asc)", value: "name:1" },
                            {
                                label: "Province Name (Desc)",
                                value: "name:-1",
                            },
                            {
                                label: "Total Not Paid (Asc)",
                                value: "totalNotPaid:1",
                            },
                            {
                                label: "Total Not Paid (Desc)",
                                value: "totalNotPaid:-1",
                            },
                        ]}
                        getOptionLabel={(opt) => opt.label}
                        getOptionValue={(opt) => opt.value}
                        size="small"
                        sx={{ minWidth: 230 }}
                    />
                    <TextField
                        size="small"
                        placeholder="Search province by name or code"
                        value={provinceSearch}
                        onChange={(e) => setProvinceSearch(e.target.value)}
                        sx={{ minWidth: 280 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Box>
            <AppTable
                columns={provinceColumns}
                data={filteredProvinceData}
                progressPending={isProvinceLoading || isProvinceFetching}
                onRowClicked={handleDrillIntoBranches}
                pagination={false}
                noDataComponent={
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            No provinces found.
                        </Typography>
                    </Box>
                }
            />
        </>
    );

    return (
        <BreadcrumbLayout
            breadcrumbs={breadcrumbs}
            headerActions={
                <Tabs
                    value={mode}
                    onChange={handleModeChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 36,
                        "& .MuiTab-root": { minHeight: 36, py: 0 },
                    }}
                >
                    {MODE_TABS.map((tab) => (
                        <Tab key={tab.value} label={tab.label} value={tab.value} />
                    ))}
                </Tabs>
            }
        >
            <BreadcrumbLayout.Error
                error={
                    mode === "provinces" && pmLevel === "provinces"
                        ? provinceError
                        : reportError
                }
            />
            <BreadcrumbLayout.Paper>
                {mode === "provinces" && pmLevel === "provinces" ? (
                    renderProvincesView()
                ) : (
                    <>
                {/* Header row */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        gap: 2,
                        alignItems: "center",
                        px: 2,
                        pt: 2,
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" sx={{ flexShrink: 0 }}>
                        Pending Branch COC Report
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "flex-end",
                            gap: 2,
                            alignItems: "center",
                        }}
                    >
                        {/* Province filter */}
                        {mode === "branches" && (
                        <Autocomplete
                            size="small"
                            options={regionOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) =>
                                option.value === value.value
                            }
                            value={
                                regionOptions.find((opt) => opt.value === region) || {
                                    value: "all",
                                    label: "All Provinces",
                                }
                            }
                            onChange={(_, newValue) => {
                                setRegion(newValue?.value || "all");
                                setBranch("all");
                            }}
                            disableClearable
                            disabled={isRegionsLoading}
                            sx={{ minWidth: 200 }}
                            renderInput={(params) => (
                                <TextField {...params} label="Province" />
                            )}
                        />
                        )}

                        {mode === "provinces" && pmLevel === "branches" && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    pr: 2,
                                    borderRight: 1,
                                    borderColor: "divider",
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                        }}
                                        noWrap
                                    >
                                        {pmProvinceName || "Province"}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        label={`${reportData?.totalItems ?? 0} Branches`}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Branch filter */}
                        <Autocomplete
                            size="small"
                            options={branchOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) =>
                                option.value === value.value
                            }
                            value={
                                branchOptions.find((opt) => opt.value === branch) || {
                                    value: "all",
                                    label: "All Branches",
                                }
                            }
                            onChange={(_, newValue) => setBranch(newValue?.value || "all")}
                            disableClearable
                            disabled={isBranchesLoading || isBranchesByRegionLoading}
                            sx={{ minWidth: 200 }}
                            renderInput={(params) => (
                                <TextField {...params} label="Branch" />
                            )}
                        />

                        {/* Sort */}
                        <BaseSelect
                            label="Sort By"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            options={[
                                { label: "Branch Name (Asc)", value: "branchName:1" },
                                { label: "Branch Name (Desc)", value: "branchName:-1" },
                                { label: "Total (Asc)", value: "totalNotPaid:1" },
                                { label: "Total (Desc)", value: "totalNotPaid:-1" },
                            ]}
                            getOptionLabel={(opt) => opt.label}
                            getOptionValue={(opt) => opt.value}
                            size="small"
                            sx={{ minWidth: 230 }}
                        />
                    </Box>
                </Box>

                {/* Summary strip */}
                {reportData?.totalNotPaid !== undefined && (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "flex-end",
                            gap: 2,
                            alignItems: "center",
                            px: 2,
                            pt: 2,
                        }}
                    >
                        <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <Box>
                                <Typography variant="body2" fontWeight="normal">
                                    Branch COC Summary:
                                </Typography>
                            </Box>
                            {summaryMetrics.map((metric, i) => (
                                <Box key={i}>
                                    <Typography variant="body2" color="text.secondary">
                                        {metric.label}
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold">
                                        Rs. {metric.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                {/* Table */}
                {isLoading || isFetching || isBranchesLoading || isBranchesByRegionLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : reportData?.data?.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            No data available for the selected filters.
                        </Typography>
                    </Box>
                ) : (
                    <AppTable
                        columns={columns}
                        data={reportData?.data || []}
                        progressPending={isLoading || isFetching}
                        paginationServer
                        paginationPerPage={perPage}
                        paginationTotalRows={reportData?.totalItems ?? 0}
                        paginationDefaultPage={currentPage}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                    />
                )}
                    </>
                )}

                <Snackbar
                    open={!!errorMessage}
                    autoHideDuration={6000}
                    onClose={() => setErrorMessage("")}
                >
                    <Alert severity="error" onClose={() => setErrorMessage("")}>
                        {errorMessage}
                    </Alert>
                </Snackbar>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default BranchCOCReport;
